import React from 'react'
import {defineMessage} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'

import {Avatar, avatarSizesMap, AvatarType} from '@vio/atlas/Avatar'
import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {Icon} from '@vio/atlas/Icon'
import mirror from '@vio/atlas/src/utils/mirror'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {getUserName} from '@vio/core/src/auth/business/authentication'
import {getUser} from '@vio/core/src/auth/modules/selectors'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {VioDealTag} from '../../../privateDeal/VioDealTag'

const IconWrapper = styled.div`
  height: 100%;

  & svg {
    ${mirror()}
  }
`

const RowLink = styled(NoStyleLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover,
  &:visited {
    text-decoration: none;
    color: inherit;
  }
`

const UserInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
`

const AvatarWrapper = styled.div`
  flex-shrink: 0;
`

const HeaderTag = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  padding: 0;
  margin-left: ${cssTheme.layout.spacing.s500};
  gap: ${cssTheme.layout.spacing.s300};
`

const SecondaryText = styled(Text)`
  color: ${cssTheme.colors.content.neutral.secondary};
`

/* Lines up with the name column; it sits outside the link so a tap on the tag does not follow it */
const TagRow = styled.div`
  margin-top: ${cssTheme.layout.spacing.s300};
  padding-inline-start: calc(
    ${avatarSizesMap.lg.size}px + ${cssTheme.layout.spacing.s500}
  );
`

interface MobileMenuUserInfoPropTypes {
  onClickEditAccount: () => void
  /** Tag under the name, for the brand's loyalty programme */
  tagSlot?: React.ReactNode
}

export const MobileMenuUserInfo = ({
  onClickEditAccount,
  tagSlot = null
}: MobileMenuUserInfoPropTypes) => {
  const {
    auth: {hasAvatarMemberTag}
  } = useBrandCapabilities()
  const user = useSelector(getUser)
  const userGivenName = user?.attributes?.given_name
  const userFamilyName = user?.attributes?.family_name
  const userName = userGivenName || userFamilyName

  return (
    <div>
      <RowLink onClick={onClickEditAccount}>
        <UserInfo>
          <AvatarWrapper>
            <Avatar
              picture={user?.attributes?.picture}
              name={getUserName(user)}
              size="lg"
              type={
                user?.attributes?.picture ? AvatarType.Picture : AvatarType.Icon
              }
            />
          </AvatarWrapper>

          <HeaderTag>
            <div>
              <Text variant="titleL" className="fs-exclude">
                {userName ? (
                  <FormattedMessageWrapper
                    id="userWidget.greeting"
                    defaultMessage="Hi, {user}"
                    values={{
                      user: userName
                    }}
                  />
                ) : (
                  <>
                    <FormattedMessageWrapper
                      id="userWidget.enterYourName"
                      defaultMessage="Enter your name"
                    />
                    <Text variant="titleL">{userGivenName}</Text>
                  </>
                )}
              </Text>
              {user?.attributes?.email ? (
                <SecondaryText className="fs-exclude" variant="bodyS">
                  {user?.attributes?.email}
                </SecondaryText>
              ) : null}
            </div>
            {hasAvatarMemberTag && (
              <VioDealTag
                message={defineMessage({
                  id: 'vioDeals.member',
                  defaultMessage: 'Vio Member'
                })}
              />
            )}
          </HeaderTag>
        </UserInfo>
        <IconWrapper>
          <Icon name="Edit" />
        </IconWrapper>
      </RowLink>
      {tagSlot && <TagRow>{tagSlot}</TagRow>}
    </div>
  )
}
